<template>
    <section>
        <!-- Popup notifications for limit reached -->
        <div class="popup-region" aria-live="assertive" aria-atomic="true" role="region" aria-label="Notifications">
            <div v-for="popup in popupNotifications" :key="popup.id"
                 :class="['grove-alert', 'grove-alert--warning', 'grove-alert--small', { 'popup-exit': popup.isExiting }]"
                 role="alert"
                 aria-atomic="true"
                 @click.stop>
                <div class="grove-alert__container">
                    <div class="grove-alert__icon" aria-hidden="true">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M10 0C4.477 0 0 4.477 0 10s4.477 10 10 10 10-4.477 10-10S15.523 0 10 0ZM10 15a1 1 0 01-1-1v-4a1 1 0 012 0v4a1 1 0 01-1 1ZM8.5 6.5a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0Z" fill="currentColor"/>
                        </svg>
                    </div>
                    <div class="grove-alert__content">
                        <h4 class="grove-alert__title">Card Limit Reached</h4>
                        <p class="grove-alert__message">You've reached the maximum of 4 cards for comparison. Remove a card to compare a new one. <span class="sr-only">(#{{ popup.id }})</span></p>
                    </div>
                    <button @click="hideSpecificPopup(popup.id)" class="grove-alert__close" aria-label="Close alert">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                </div>
            </div>
        </div>
        <!-- Background overlay for click-outside functionality -->
        <div v-if="popupNotifications.length > 0" class="popup-backdrop" aria-hidden="true" tabindex="-1" @click="hideAllPopups"></div>
        <main class="main-wrapper" @click="hideSearchResults($event.target)">
            <compliance v-if="IsCompliance"></compliance>
            <h1 class="heading-xl">Card Comparison</h1>
            <p class="subheading">Use our card comparison tool to review up to four credit cards provided by our partners.</p>

            <!-- Crossfade container to prevent layout shifts -->
            <div class="content-crossfade-container">
                <!-- Skeleton loader with absolute positioning -->
                <div class="skeleton-layer" :class="{ 'fade-out': !isLoading }">
                    <skeleton-loader :card-count="getSkeletonCardCount()"></skeleton-loader>
                </div>

                <!-- Actual content with absolute positioning -->
                <div class="content-layer" :class="{ 'fade-in': !isLoading }">
                    <search-bar :cards="cards" :external-limit-reached="searchBarExternalLimit" ref="searchBar" @limit-reached="showPopupError('search-bar')"></search-bar>
                    <category-lookup :cards="cards" :external-limit-reached="categoryExternalLimit" @limit-reached="showPopupError('category-lookup')"></category-lookup>
                    <div class="advertiser-disclosure">
                        <a href="javascript:void(0)" :target="'#lt-advertiser-disclosure-comparison'" @click.prevent="openModal($event.target.target)">Advertiser Disclosure</a>
                    </div>
                    <div class="compare-cards">
                        <more-than-one-card v-if="IsCards" :cards="updatedCards" :page-request-id="pageRequestId" @card-removed="removeCardFromCompare" :isSticky="isSticky" :is-compliance="IsCompliance"></more-than-one-card>
                        <one-card v-else-if="IsOneCard" :cards="updatedCards" @add-saved-cards="addCardToCompare" @card-removed="removeCardFromCompare" :isSticky="isSticky" :is-compliance="IsCompliance"></one-card>
                        <no-cards v-else @add-saved-cards="addCardToCompare"></no-cards>
                    </div>
                    <div class="general-disclaimer">
                        <p>General Disclaimer: See the online credit card application for details about terms and conditions. We make every effort to maintain accurate information. However, all credit card information is presented without warranty. To confirm terms and conditions, click the "Apply Now" button and review info on the secure credit card terms page.</p>
                    </div>
                </div>
            </div>
        </main>
    </section>
</template>
<style src="../../shared/sass/lt-main.scss" lang="scss"></style>
<style src="../../shared/sass/saved-cards/app.scss" lang="scss"></style>
<style scoped>
.popup-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.1);
    z-index: 9998;
    pointer-events: auto;
}

/* Crossfade container for skeleton loading */
.content-crossfade-container {
    position: relative;
    min-height: 1200px;
    box-sizing: border-box;
    width: 100%;
}

.skeleton-layer,
.content-layer {
    position: relative;
    width: 100%;
    min-height: 1200px;
    box-sizing: border-box;
    transition: opacity 0.2s ease-in-out; /* Slightly slower for stability */
}

.skeleton-layer {
    opacity: 1;
    z-index: 1;
}

.skeleton-layer.fade-out {
    opacity: 0;
    pointer-events: none;
    display: none;
}

.content-layer {
    opacity: 0;
    z-index: 2;
}

.content-layer.fade-in {
    opacity: 1;
}
</style>

<script>
    import PageService from '../../shared/services/page-service';
    import NoCards from './NoCards.vue';
    import MoreThanOneCard from './MoreThanOneCard.vue';
    import OneCard from './OneCard.vue';
    import SearchBar from './SearchBar.vue';
    import CategoryLookup from './CategoryLookup.vue';
    import Compliance from '../shared/Compliance.vue';
    import SkeletonLoader from '../shared/skeletons/SkeletonLoader.vue';

    import MetaTags from '../../shared/mixins/meta-tags';
    import SavedCompareCardsMixin from '../../shared/mixins/saved-compare-cards';
    import CardService from '../../shared/services/card-service';

    export default {
        name: 'app',
        props: {
            pageVariation: {
                type: Object,
                required: false
            }
        },
        data: function () {
            return {
                pageData: null,
                cards: [],
                updatedCards: [],
                popupNotifications: [],
                popupIdCounter: 0,
                searchBarExternalLimit: false,
                categoryExternalLimit: false,
                isLoading: true,
                initialDataLoaded: false,
            };
        },
        watch: {
            // whenever cards array is updated, this function will run to reorder and add "best" labels accordingly
            cards: function (newCards, oldCards) {
                // Reset external limit flags when cards are removed
                if (oldCards && newCards.length < oldCards.length) {
                    this.searchBarExternalLimit = false;
                    this.categoryExternalLimit = false;
                }
                
                if (newCards && newCards.length > 0) {
                    //for adobe analytics tracking
                    var trackData = { creditCard_data: [] };
                    var category = this.$analyticsService.getCategoryName();
                    newCards.forEach((card, index) => {
                        var trackCard = {
                            card_id: card.cardId,
                            card_page: location.href,
                            card_position: (++index),
                            card_section: category,
                            category: category,
                        }
                        trackData.creditCard_data.push(trackCard);
                    });
                    this.$analyticsService.trackCTA(trackData, "Credit Card Impression");

                    //for updating "best" labels
                    CardService.getSavedCards(newCards).then((res) => {
                        if (res && res.data) {
                            this.updatedCards = res.data;
                        }
                        else {
                            console.log("There was an error reordering cards.");
                        }
                    });
                }
            },
        },
        mixins: [MetaTags, SavedCompareCardsMixin],
        computed: {
            pageRequestId: function () {
                return pageRequestId || "";
            },
            IsCards: function () {
                return this.cards && this.cards.length > 1;
            },
            IsOneCard: function () {
                return this.cards && this.cards.length === 1;
            },
            IsCompliance: function () {
                return window.location.href.includes('/compliance/');
            }
        },
        methods: {
            getSkeletonCardCount: function() {
                // Determine skeleton card count based on expected data
                if (this.pageVariation && this.pageVariation.id !== null) {
                    const expectedCards = this.pageVariation.sections?.flatMap(section =>
                        section.itemCollection?.flatMap(itemArray => itemArray.items || [])
                    )?.filter(item => item.itemType === "card" && item.card)?.length || 0;
                    return Math.min(expectedCards, 4);
                }

                // Check for existing saved cards
                const savedCards = localStorage.getItem('cardItems') ? JSON.parse(localStorage.getItem('cardItems')) : [];
                if (savedCards && savedCards.length > 0) {
                    return Math.min(savedCards.length, 4);
                }

                // Default to 3 cards skeleton
                return 3;
            },
            setLoadingState: function(loading) {
                this.isLoading = loading;
            },
            getUpdatedCards: function (cardList) {
                let updatedCards = localStorage.getItem('cardItems') ? JSON.parse(localStorage.getItem('cardItems')) : [];

                cardList.forEach((card) => {
                    var cardIndex = updatedCards.findIndex(f => f.CardId == card.cardId);
                    if (cardIndex === -1) {
                        var cardDetails = {
                            id: card.legacyId,
                            CardId: card.cardId,
                            CardName: card.name,
                            Highlight: {
                                Label: card.editorialContent.highlights[0].label,
                                Text: card.editorialContent.highlights[0].text
                            },
                            RedirectLink: card.redirectLink,
                            ImageUrl: card.imageUrl,
                            DetailsUrl: card.detailsUrl,
                            IsRateFees: card.issuer.ratesAndFeesIndicator,
                            IsRateFeesTermsApply: card.issuer.ratesAndFeesTermsApplyIndicator,
                            BankTermsUrl: card.bankTermsUrl
                        }
                        updatedCards.push(cardDetails);
                    }
                });
                return updatedCards;
            },
            showPopupError: function(source) {
                // Mark that limit has been reached externally for the opposite component
                if (source === 'category-lookup') {
                    this.searchBarExternalLimit = true;
                } else if (source === 'search-bar') {
                    this.categoryExternalLimit = true;
                }
                
                // Only show one popup at a time
                if (this.popupNotifications.length === 0) {
                    const newPopup = {
                        id: ++this.popupIdCounter,
                        isExiting: false
                    };
                    this.popupNotifications.push(newPopup);
                    // Popup will persist until user interaction (X button or click outside)
                }
            },
            hideSpecificPopup: function(popupId) {
                const popup = this.popupNotifications.find(p => p.id === popupId);
                if (popup && !popup.isExiting) {
                    popup.isExiting = true;
                    
                    // Remove popup after exit animation completes
                    setTimeout(() => {
                        const index = this.popupNotifications.findIndex(p => p.id === popupId);
                        if (index > -1) {
                            this.popupNotifications.splice(index, 1);
                        }
                    }, 300); // Match animation duration
                }
            },
            hideAllPopups: function() {
                // Hide each popup individually to maintain consistent animation behavior
                this.popupNotifications.forEach(popup => {
                    if (!popup.isExiting) {
                        this.hideSpecificPopup(popup.id);
                    }
                });
            },
            addCardToCompare: function (card) {
                //add this cards array to vue state so it will be available in another component as well for data binding
                var sessionCards = this.getUpdatedCards([card]);
                if (sessionCards.length > 0) {
                    localStorage.setItem('cardItems', JSON.stringify(sessionCards));
                    this.moreThanOneCards(sessionCards);
                }
            },
            removeCardFromCompare: function(card) {
                var cardIdValue = card.cardId;
                var savedCreditCards = localStorage.getItem('cardItems') ? JSON.parse(localStorage.getItem('cardItems')) : [];
                var indexOfSession = savedCreditCards.findIndex(f => f.cardId == cardIdValue);
                if (indexOfSession !== -1) {
                    savedCreditCards.splice(indexOfSession, 1);
                    localStorage.setItem('cardItems', JSON.stringify(savedCreditCards));
                }
                this.cards = savedCreditCards;
            },
            closeModal: function(modal) {
                const backdrop = document.querySelector('.modal-backdrop.fade.show');
                modal.setAttribute('aria-hidden', 'true');
                backdrop.classList.remove('show');
                setTimeout(() => {
                modal.classList.remove('show');
                if (!document.querySelector('.modal.show')) {
                    document.body.classList.remove('modal-open');
                }
                });
                setTimeout(() => {
                modal.style.display = 'none';
                    backdrop.remove();
                }, 500);
            },
            hideSearchResults: function(el) {
                var searchInput = document.querySelector('#card-search');
                var searchResults = document.querySelector('.results-container')

                if (searchResults) {
                    if (el != searchInput && el != searchResults) {
                        searchResults.style.display = 'none';
                    } else {
                        searchResults.style.display = 'block';
                    }
                }
            },
            handleKeydown: function (event) {
                if (event && (event.key === 'Escape' || event.key === 'Esc')) {
                    if (this.popupNotifications && this.popupNotifications.length > 0) {
                        const last = this.popupNotifications[this.popupNotifications.length - 1];
                        if (last) {
                            this.hideSpecificPopup(last.id);
                        }
                    }
                }
            },
            isSticky: function(el) {
                if (!el) return;
                if (!el._sticky) {
                    const stickyElementTop = parseInt(getComputedStyle(el).top, 10) || 0;
                    el._sticky = { top: stickyElementTop, pinned: el.classList.contains('pinned') };
                }
                const H = 2;
                const y = el.getBoundingClientRect().top;
                const top = el._sticky.top;
                if (y <= top && !el._sticky.pinned) {
                    el.classList.add('pinned');
                    el._sticky.pinned = true;
                } 
                else if (y > top + H && el._sticky.pinned) {
                    el.classList.remove('pinned');
                    el._sticky.pinned = false;
                }
            }
        },
        components: {
            NoCards,
            MoreThanOneCard,
            OneCard,
            SearchBar,
            CategoryLookup,
            Compliance,
            SkeletonLoader,
        },
        created: function () {
            const savedCards = localStorage.getItem('cardItems') ? JSON.parse(localStorage.getItem('cardItems')) : [];
            const hasExistingData = savedCards && savedCards.length > 0;

            this.setLoadingState(true);

            // Adaptive loading time: ensure skeleton is visible for good UX
            const minLoadingTime = hasExistingData ? 100 : 300;
            const startTime = Date.now();

            const finishLoading = () => {
                const elapsedTime = Date.now() - startTime;
                const remainingTime = Math.max(0, minLoadingTime - elapsedTime);

                setTimeout(() => {
                    this.setLoadingState(false);
                    this.initialDataLoaded = true;
                }, remainingTime);
            };

            PageService.getPageData('saved-credit-cards').then((res) => {
                if (res && res.data) {
                    this.pageData = res.data;
                }
                else {
                    console.log("PAGE DOES NOT EXIST, SO ADD IT TO THE ADMIN");
                }
            });
            if (this.pageVariation.id !== null) {
                let pageVariationCards = this.pageVariation.sections.flatMap(section => section.itemCollection.flatMap(itemArray => itemArray.items || []))
                    .filter(item => item.itemType === "card" && item.card)
                    .map(item => item.card)
                    .slice(0, 4);

                if (pageVariationCards && pageVariationCards.length > 0) {
                    localStorage.setItem('cardItems', JSON.stringify(pageVariationCards));
                    this.moreThanOneCards(pageVariationCards);
                }
                finishLoading();
            } else {
                let savedCards = localStorage.getItem('cardItems') ? JSON.parse(localStorage.getItem('cardItems')) : [];
                if (savedCards && savedCards.length > 0) {
                    this.moreThanOneCards(savedCards);
                    finishLoading();
                } else {
                    finishLoading();
                }
            }
        },
        mounted: function () {
            //for adobe analytics tracking
            this.$analyticsService.pageView();
            // a11y: allow ESC to dismiss the latest popup (bind to Vue instance)
            this._boundHandleKeydown = this.handleKeydown.bind(this);
            window.addEventListener('keydown', this._boundHandleKeydown);
            document.querySelectorAll('[data-dismiss=modal]').forEach((element) => {
                element.addEventListener('click', (event) => {
                    event.preventDefault();
                    const modal = document.querySelector('.modal.show');
                    if (modal) {
                        this.closeModal(modal);
                    }
                });
            });

            // window.addEventListener('scroll', this.scrollPassed);
        },
        updated: function() {
            // syncing the scrolling between both grid containers
            const gridContainers = document.querySelectorAll('.grid-container');
            gridContainers.forEach((el) => {
                el.addEventListener('scroll', function() {
                    gridContainers.forEach((el) => {
                        el.scrollLeft = this.scrollLeft;
                    });
                })
            });
        },
        beforeDestroy: function () {
            if (this._boundHandleKeydown) {
                window.removeEventListener('keydown', this._boundHandleKeydown);
            }
        },
    };
</script>
